import { Box, Card, CardContent } from "@mui/material";
import { GridSelectionModel, itIT } from "@mui/x-data-grid";
import { HttpStatusCode } from "axios";
import React from "react";
import Swal, { SweetAlertOptions } from 'sweetalert2';
import PageInfoRequest from "../../../model/common/PageInfoRequest";
import PageInfoResponse from "../../../model/common/PageInfoResponse";
import Keyword from "../../../model/database/Keyword";
import SustainableDevelopmentGoal from "../../../model/database/SustainableDevelopmentGoal";
import KeywordFilters from "../../../model/filters/KeywordFilters";
import CategoriesTableColumns from "../../../model/table/column/CategoriesTableColumns";
import { KeywordRow } from "../../../model/table/row/KeywordRow";
import keywordSortFields from "../../../model/table/sort/KeywordSortFields";
import AuthContext from "../../../route/AuthContext";
import CategoryService from "../../../services/CategoryService";
import CustomSweetalert from "../../../theme/sweetalert";
import { StyledTable } from "../../common/table/StyledTable";
import CategoriesModal from "./CategoriesModal";
import CategoriesTableToolbar from "./CategoriesTableToolbar";


const CategoriesTable = () => {

  const authContext = React.useContext(AuthContext);

  const [idSelected, setIdSelected] = React.useState<GridSelectionModel>([]);
  const [pageInfoResponse, setPageInfoResponse] = React.useState<PageInfoResponse>({
    number: 0,
    size: 0,
    totalElements: 0,
    totalPages: 0
  });
  const [pageInfo, setPageInfo] = React.useState<PageInfoRequest>({
    page: 0,
    size: 10,
    sort: keywordSortFields[0].value + ',asc'
  });
  const [filterRequest, setFilterRequest] = React.useState<KeywordFilters>({});

  const [loading, setLoading] = React.useState<boolean>(false);
  const [rows, setRows] = React.useState<SustainableDevelopmentGoal[]>([]);
  const [sdgs, setSdgs] = React.useState<SustainableDevelopmentGoal[]>([]);
  const [openModal, setOpenModal] = React.useState<boolean>(false);
  const [sdg, setSdg] = React.useState<SustainableDevelopmentGoal | undefined>(undefined);

  const handleEdit = async (id: string) => {
    setSdg(sdgs.find(value => value.id === id));
    setOpenModal(true);
  }

  const handleSave = (keywordToSave: Keyword) => {
    return new Promise<boolean>(async resolve => {

      let [status, response] = [undefined, undefined];
      if (!keywordToSave.id) {
        [status, response] = await CategoryService.save(authContext, keywordToSave);
      } else {
        [status, response] = await CategoryService.update(authContext, keywordToSave);
      }

      if (status && response && status === HttpStatusCode.Ok) {
        let swOptions: SweetAlertOptions = {
          title: 'Completato',
          text: 'Categoria aggiunta correttamente.',
          icon: 'success'
        };

        switch (response) {
          case 'SAVED':
            resolve(true);
            handleCloseModal();
            break;
          case 'UPDATED':
            resolve(true);
            swOptions.text = 'Categoria aggiornata correttamente.';
            handleCloseModal();
            break;
          case 'NAME_EXISTS':
            resolve(false);
            swOptions.title = 'Attenzione';
            swOptions.text = 'Il nome inserito esiste già.';
            swOptions.icon = 'warning';
            break;
          default:
            resolve(false);
            swOptions.title = 'Errore';
            swOptions.text = 'Si è verificato un problema durante il salvataggio.';
            swOptions.icon = 'error';
            break;
        }
        CustomSweetalert(swOptions).then(() => {
          reloadData();
        });
      }
    });
  }

  const handleCloseModal = () => setOpenModal(false);

  const handleDelete = (id: string) => {
    Swal.fire({
      title: 'Sei sicuro?',
      text: "L'operazione è irreversibile. I dati non potranno essere recuperati.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d32f2f',
      confirmButtonText: 'Elimina',
      cancelButtonText: 'Annulla',
      reverseButtons: true
    }).then(async (result) => {
      if (result.isConfirmed) {
        const [status, response] = await CategoryService.deleteById(authContext, id);
        if (status && response && status === HttpStatusCode.Ok) {
          let swOptions: SweetAlertOptions = {
            title: 'Completato',
            text: 'Categoria eliminata correttamente.',
            icon: 'success'
          };

          switch (response) {
            case 'DELETED':
              break;
            default:
              swOptions.title = 'Errore';
              swOptions.text = 'Non è stato possibile eliminare l\'elemento selezionato.';
              swOptions.icon = 'error';
              break;
          }
          CustomSweetalert(swOptions).then(() => {
            reloadData();
          });
        } else
          CustomSweetalert({
            title: 'Attenzione',
            text: 'Non è stato possibile eliminare l\'elemento selezionato.',
            icon: 'error'
          })
      }
    });
  };

  const mapResponseToRows = (response: any) => {
    return [...response._embedded.sdgs].map(value => {
      let row: KeywordRow = {
        id: value.id,
        name: value.name,
        description: value.description !== null ? (value.description.length > 100 ? value.description.substring(100) + '...' : value.description) : ''
      };
      return row;
    });
  }

  const applyFilters = (filters: KeywordFilters) => {
    setFilterRequest(filters);
  }

  const applySort = (value: string) => {
    if (pageInfo.sort !== value) {
      setPageInfo((prevState) => ({ ...prevState, sort: value }));
    }
  }

  const reloadData = async () => {
    setLoading(true);
    let [status, response] = [undefined, undefined];
    if (Object.keys(filterRequest).length === 0)
      [status, response] = await CategoryService.getAll(authContext, pageInfo);
    else
      [status, response] = await CategoryService.getAllFiltered(authContext, pageInfo, filterRequest);

    if (status && response && status === HttpStatusCode.Ok) {
      setPageInfoResponse((response as any).page);
      setSdgs((response as any)._embedded.sdgs);
      setRows(mapResponseToRows(response));
    } else {
      CustomSweetalert({
        title: 'Attenzione',
        text: 'Si è verificato un\'errore durante la comunicazione con il server remoto.',
        icon: 'error'
      });
    }

    setLoading(false);
  };

  React.useEffect(() => {
    reloadData();
  }, [pageInfo, filterRequest]);

  return (
    <Card sx={{ minWidth: 275, mt: 2, mb: 2 }}>
      <CardContent>
        <CategoriesTableToolbar
          handleNew={() => {
            setSdg(undefined);
            setOpenModal(true);
          }}
          numSelected={idSelected.length}
          applyFilters={applyFilters}
          applySort={applySort}
        />
        <Box sx={{ height: 'auto', width: '100%' }}>
          <StyledTable
            loading={loading}
            paginationMode="server"
            rows={rows}
            columns={CategoriesTableColumns({
              editCallback: handleEdit,
              deleteCallback: handleDelete
            })}
            autoHeight
            rowsPerPageOptions={[10, 15, 20]}
            pageSize={pageInfo.size}
            onPageSizeChange={newPageSize => setPageInfo((prevState) => ({ ...prevState, size: newPageSize }))}
            onPageChange={newPage => setPageInfo((prevState) => ({ ...prevState, page: newPage }))}
            rowCount={pageInfoResponse.totalElements}
            localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
            // onSelectionModelChange={selectionModel => setIdSelected(selectionModel)}
            // checkboxSelection
            getRowHeight={() => 'auto'}
            disableSelectionOnClick
            experimentalFeatures={{ newEditingApi: false }}
          />
        </Box>
      </CardContent>
      <CategoriesModal
        open={openModal}
        category={sdg}
        handleClose={handleCloseModal}
        handleSave={handleSave}
      />
    </Card>
  )
}

export default CategoriesTable;
